import { useRef } from 'react';

/**
 * Keep showing the last non-null value while a closing UI element (e.g. an MUI
 * `Dialog`) plays its exit animation.
 *
 * Many of our modals receive their data by id from a parent (`agreementRequestId`,
 * `closeRetainerModal`, etc.). On close the parent clears that id, which makes
 * the looked-up object briefly become `null` while the dialog is still mounted
 * for its ~225ms exit transition. Without this hook, the dialog flashes its
 * "not found" / "no item selected" empty branch for one frame.
 *
 * Semantics:
 * - When `value` is non-null, it is captured and returned.
 * - When the consumer is closing (`isOpen === false`) and `value` is null, the
 *   most recently seen non-null value is returned so the exit animation keeps
 *   its real contents.
 * - When the consumer is open (`isOpen === true`) and `value` is null, `null`
 *   is returned. This preserves real loading / not-found states on a fresh
 *   open (e.g. a deep-link before the query resolves).
 *
 * Render-time mutation of the ref is intentional and idempotent (always set
 * to the current `value`), which is safe under React strict mode.
 */
export function useLastVisible<T>(
  value: T | null | undefined,
  isOpen: boolean,
): T | null {
  const lastSeenRef = useRef<T | null>(null);
  if (value !== null && value !== undefined) {
    lastSeenRef.current = value;
    return value;
  }
  return isOpen ? null : lastSeenRef.current;
}
